// SPDX-FileCopyrightText: 2022-2026 Lablup Inc. All rights reserved.
// SPDX-License-Identifier: LicenseRef-Proprietary

export { logApiError } from "../hooks/apiError";

/**
 * FastTrack's DRF error envelope, emitted by `envelope_exception_handler`
 * (`workflow/common/api/exceptions.py`). Hand-written on purpose — the wire
 * format is locked to the backend pydantic models by the golden fixtures
 * under `__fixtures__/error-envelope/`, exercised in
 * `__tests__/errorEnvelope.fixtures.test.ts`.
 */
export interface ErrorEnvelopeDetail {
  message: string;
  /** Dotted field path; absent for non-field errors. */
  field?: string;
  code?: string;
}

export interface ErrorEnvelope {
  error: {
    /** Backend `ErrorCode` enum value, e.g. `"authentication_required"`. */
    code: string;
    message: string;
    /** Present only for validation errors. */
    details?: ErrorEnvelopeDetail[];
  };
}

/**
 * FastTrack backend `ErrorCode` values the frontend references. Kept in
 * lockstep with the enum by `__fixtures__/error-codes.json` (rendered from
 * the backend) and the sync test in
 * `__tests__/errorEnvelope.fixtures.test.ts`. Backend.AI Manager codes
 * (e.g. `storage_access_forbidden`) are a different namespace and do not
 * belong here.
 *
 * `loginMethodDisabled` and `passwordExpired` drive branching in
 * `hooks/auth.tsx`; `activeWebuiSession` is not branched on (the 409 branch
 * keys on status, which also covers pre-envelope servers) — it is registered
 * here so the enum-sync test locks the code's spelling for consumers that
 * may branch on it later.
 */
export const ERROR_CODES = {
  loginMethodDisabled: "login_method_disabled",
  passwordExpired: "password_expired",
  activeWebuiSession: "active_webui_session",
} as const;

/**
 * Structured representation of an API error.
 */
export interface ApiError {
  /** Human-readable error message */
  message: string;
  /** HTTP status code, if available */
  status?: number;
  /** The endpoint that was called */
  endpoint?: string;
  /** Backend error code (maps to backend ErrorCode enum) */
  code?: string;
  /** Backend.AI problem+json `type` URI; fallback for managers < 25.10.0. */
  errorType?: string;
}

/**
 * An `ApiError` that is also a real `Error`.
 *
 * Throw sites must reject with this, not a bare object: call sites narrow with
 * `err instanceof Error ? err.message : String(err)`, and a plain object takes
 * the `String(err)` branch and renders `[object Object]`.
 */
export class ApiResponseError extends Error implements ApiError {
  readonly status?: number;
  readonly endpoint?: string;
  readonly code?: string;
  readonly errorType?: string;

  constructor(error: ApiError) {
    super(error.message);
    this.name = "ApiResponseError";
    this.status = error.status;
    this.endpoint = error.endpoint;
    this.code = error.code;
    this.errorType = error.errorType;
  }
}

/** `str(ErrorCode(STORAGE, ACCESS, FORBIDDEN))` — stable since Core 25.10.0. */
const STORAGE_ACCESS_FORBIDDEN_CODE = "storage_access_forbidden";

/** Tail of the problem+json `type` URI, for managers that predate `error_code`. */
const STORAGE_PERMISSION_PROBLEM_TYPE = "storage-permission-not-allowed";

/**
 * True when the vfolder host's policy does not grant the required permission.
 * The denial is permanent until an admin changes the resource policy.
 *
 * Never key on the HTTP status: `InsufficientStoragePermission` extends
 * `web.HTTPBadRequest` on the manager, so this arrives as 400, not 403.
 */
export function isStorageHostPermissionDenied(error: unknown): boolean {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  const { code, errorType } = error as Partial<ApiError>;
  // Either signal suffices: a manager that renamed the enum would otherwise
  // skip the `type` fallback and silently regress to a red load-failed alert.
  return (
    code === STORAGE_ACCESS_FORBIDDEN_CODE ||
    (typeof errorType === "string" &&
      errorType.endsWith(STORAGE_PERMISSION_PROBLEM_TYPE))
  );
}

/**
 * Parse an unknown error into a structured ApiError.
 *
 * Handles:
 * - AbortError (timeout / unmount cancellation)
 * - TypeError (network failures, DNS, CORS)
 * - Error with message
 * - Non-Error values (strings, objects, etc.)
 */
export function parseApiError(error: unknown, endpoint?: string): ApiError {
  if (error instanceof ApiResponseError) {
    // Must precede the `instanceof Error` branch, which would drop these fields.
    return {
      message: error.message || "An unexpected error occurred",
      status: error.status,
      endpoint: endpoint ?? error.endpoint,
      code: error.code,
      errorType: error.errorType,
    };
  }

  if (error instanceof DOMException && error.name === "AbortError") {
    return {
      message: "Request was cancelled",
      endpoint,
    };
  }

  if (error instanceof TypeError) {
    // fetch throws TypeError for network failures (DNS, CORS, offline)
    return {
      message: "Network error — please check your connection",
      endpoint,
    };
  }

  if (error instanceof Error) {
    return {
      message: error.message || "An unexpected error occurred",
      endpoint,
    };
  }

  if (typeof error === "string") {
    return {
      message: error,
      endpoint,
    };
  }

  return {
    message: "An unexpected error occurred",
    endpoint,
  };
}

/**
 * Parse an HTTP response into a structured ApiError.
 * Call this when response.ok is false.
 */
export async function parseResponseError(
  response: Response,
  endpoint?: string
): Promise<ApiError> {
  const base: ApiError = {
    message: `Request failed (${response.status})`,
    status: response.status,
    endpoint,
  };

  try {
    const body = await response.json();
    // `error.message` / `error.code` is FastTrack's DRF envelope
    // (`envelope_exception_handler`) and wins when present; `msg` / `title` /
    // `error_code` / `type` are Backend.AI problem+json; the rest are legacy
    // top-level FastTrack shapes. `msg` beats `title` because it names the
    // offending permission and host, where `title` is only the error class.
    // An empty string must not win, or it blanks the `Request failed (N)`
    // default and leaves call sites interpolating nothing into their toast.
    const firstText = [
      body?.error?.message,
      body?.message,
      body?.detail,
      body?.error,
      body?.msg,
      body?.title,
    ].find((value) => typeof value === "string" && value.length > 0);
    if (firstText !== undefined) {
      base.message = firstText;
    }
    if (typeof body?.error?.code === "string" && body.error.code.length > 0) {
      base.code = body.error.code;
    } else if (typeof body?.error_code === "string") {
      base.code = body.error_code;
    } else if (typeof body?.code === "string") {
      base.code = body.code;
    }
    if (typeof body?.type === "string") {
      base.errorType = body.type;
    }
    // `traceback` (on `debug.enabled` deployments) is deliberately never read.
  } catch {
    // Response body is not JSON — use the default message
  }

  return base;
}
